import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { Device } from '@capacitor/device';
import { IonIcon, useIonAlert, useIonToast } from '@ionic/react';
import { scanOutline, closeOutline, checkmarkCircle, alertCircle } from 'ionicons/icons';
import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import axios from '../axios';
import { constant, requestPermissions } from '../utils';

interface ScanButtonProps {
  isAssetPage: boolean;
}

type ScanStatus = 'idle' | 'scanning' | 'success' | 'error';

const ScanButton: React.FC<ScanButtonProps> = ({ isAssetPage }) => {
  const isMounted = useRef(true);
  const history = useHistory();
  const [presentAlert] = useIonAlert();
  const { t } = useTranslation();
  const [present] = useIonToast();
  const [showIcon, setShowIcon] = useState(true);
  const [status, setStatus] = useState<ScanStatus>('idle');
  const [isPressed, setIsPressed] = useState(false);

  useEffect(() => {
    localStorage.setItem(constant.QUICK_SCAN_ASSET_CODE, '');
    isMounted.current = true;
    (async () => {
      const info = await Device.getInfo();
      if (info?.platform === 'web' && isMounted.current) {
        setShowIcon(false);
        return;
      }
    })();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleScan = (data: any) => {
    if (data) {
      try {
        checkForAsset(data);
      } catch {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 2000);
        present({ message: t('Utils.fetchFail'), color: 'danger', duration: 4000 });
      }
    }
  };

  const openScanner = async () => {
    if (status === 'scanning') return;
    try {
      const supportResult = await BarcodeScanner.isSupported();
      if (!supportResult.supported) {
        present({ message: t('Utils.unsupportDevice'), duration: 2000 });
        return;
      }

      const granted = await requestPermissions();
      if (!granted) {
        present({ message: t('Utils.noPermission'), duration: 2000 });
        return;
      }

      const check = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
      if (!check.available) {
        present({ message: t('Utils.settingScanner'), duration: 2000 });
        await BarcodeScanner.installGoogleBarcodeScannerModule();
      }

      setStatus('scanning');
      const { barcodes } = await BarcodeScanner.scan();
      const scannedValue = barcodes[0]?.displayValue;
      if (scannedValue) {
        setStatus('success');
        setTimeout(() => setStatus('idle'), 1500);
        handleScan(scannedValue);
      } else {
        setStatus('idle');
      }
    } catch (error: any) {
      // User cancelled scanning — silently reset
      setStatus('idle');
    }
  };

  const checkForAsset = async (id: any) => {
    const config: any = {
      method: 'get',
      url: `resource/Asset?fields=["*"]&filters=[["Asset","barcode_id","=","${id}"]]`,
      headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
    };
    try {
      const temp = await axios(config);
      if (temp?.data?.data?.length) {
        history.push({ pathname: '/assetDetail', state: temp?.data?.data[0]?.name });
      } else {
        present({ message: t('Utils.AssetNotExist'), duration: 2000 });
        if (isAssetPage) askAddAsset(id);
      }
    } catch (error: any) {
      if (error.response?.status) {
        checkService(id);
      } else {
        present({ message: t('Utils.noScanFound'), duration: 2000, color: 'danger' });
      }
    }
  };

  const checkService = async (id: any) => {
    const config: any = {
      method: 'get',
      url: `/resource/Repair Request/${id}`,
      headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
    };
    try {
      await axios(config);
      history.push({ pathname: '/serviceDetail', state: id });
    } catch (error: any) {
      if (error.response?.status) {
        checkMaintenance(id);
      } else {
        present({ message: t('Utils.noScanFound'), duration: 2000, color: 'danger' });
      }
    }
  };

  const checkMaintenance = async (id: any) => {
    const config: any = {
      method: 'get',
      url: `/resource/Maintenance Request/${id}`,
      headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
    };
    try {
      await axios(config);
      history.push({ pathname: '/maintainanceDetail', state: id });
    } catch (error: any) {
      present({ message: t('Utils.noScanFound'), duration: 2000 });
      history.push({ pathname: '/home' });
    }
  };

  const askAddAsset = (id: any) => {
    presentAlert({
      header: t('Utils.alertAssetAdd'),
      buttons: [
        { text: t('Utils.cancel'), role: 'cancel' },
        {
          text: t('Utils.yesCreate'),
          role: 'confirm',
          handler: () => history.push({ pathname: '/createAsset', state: id }),
        },
      ],
    });
  };

  const getButtonColor = () => {
    if (status === 'success') return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    if (status === 'error') return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    if (status === 'scanning') return 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
    return 'linear-gradient(135deg, #1d508d 0%, #0f3460 100%)';
  };

  const getButtonIcon = () => {
    if (status === 'success') return checkmarkCircle;
    if (status === 'error') return alertCircle;
    if (status === 'scanning') return closeOutline;
    return scanOutline;
  };

  if (!showIcon) return null;

  return (
    <>
      <style>{`
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1.65); opacity: 0; }
        }
        @keyframes pulse-ring-2 {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes scan-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fab-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes icon-pop {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .scan-fab-btn {
          animation: fab-bounce 3s ease-in-out infinite;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .scan-fab-btn:active {
          transform: scale(0.92) !important;
          animation: none !important;
        }
        .status-icon-anim {
          animation: icon-pop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      {/* Floating Action Button */}
      <div
        style={{
          position: 'fixed',
          bottom: '28px',
          right: '22px',
          zIndex: 999,
        }}
      >
        {/* Pulse rings — only when idle */}
        {status === 'idle' && (
          <>
            <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '2px solid rgba(29, 80, 141, 0.55)',
              animation: 'pulse-ring 2s ease-out infinite',
            }} />
            <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '2px solid rgba(29, 80, 141, 0.3)',
              animation: 'pulse-ring-2 2s ease-out infinite 0.4s',
            }} />
          </>
        )}

        {/* Scanning spinner ring */}
        {status === 'scanning' && (
          <div style={{
            position: 'absolute',
            inset: '-6px',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: '#60a5fa',
            borderRightColor: '#60a5fa',
            animation: 'scan-spin 0.7s linear infinite',
            pointerEvents: 'none',
          }} />
        )}

        {/* Main button */}
        <button
          id="scan-fab-button"
          className="scan-fab-btn"
          onClick={openScanner}
          onMouseDown={() => setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          onTouchStart={() => setIsPressed(true)}
          onTouchEnd={() => setIsPressed(false)}
          disabled={status === 'success' || status === 'error'}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: getButtonColor(),
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: status === 'idle'
              ? '0 8px 28px rgba(29, 80, 141, 0.45), 0 2px 8px rgba(0,0,0,0.2)'
              : status === 'success'
                ? '0 8px 28px rgba(16, 185, 129, 0.5)'
                : status === 'error'
                  ? '0 8px 28px rgba(239, 68, 68, 0.5)'
                  : '0 8px 28px rgba(59, 130, 246, 0.5)',
            transition: 'background 0.3s ease, box-shadow 0.3s ease',
            padding: 0,
            position: 'relative',
          }}
          aria-label="Scan barcode"
        >
          <IonIcon
            key={status}
            icon={getButtonIcon()}
            className="status-icon-anim"
            style={{
              fontSize: '26px',
              color: '#ffffff',
            }}
          />
        </button>

        {/* Tooltip label */}
        <div style={{
          position: 'absolute',
          right: '70px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(15, 25, 55, 0.88)',
          backdropFilter: 'blur(12px)',
          color: '#fff',
          fontSize: '12px',
          fontWeight: 600,
          padding: '5px 11px',
          borderRadius: '20px',
          whiteSpace: 'nowrap',
          opacity: status === 'scanning' ? 1 : 0,
          transition: 'opacity 0.25s ease',
          pointerEvents: 'none',
          letterSpacing: '0.02em',
        }}>
          Scanning…
        </div>
      </div>
    </>
  );
};

export default ScanButton;
